import FormControl from '~/common/componentMapper/FormControl';
import FormErrorMessage from '~/common/componentMapper/FormErrorMessage';
import FormLabel from '~/common/componentMapper/FormLabel';
import ExtractionDataIcon from '~/common/componentMapper/QuestionExtraction/components/ExtractionDataIcon';
import Flex from '~/common/components/Flex';
import GenericTooltip from '~/common/components/GenericTooltip';
import { parseLabelMarkup } from '~/common/util/stringUtils';

import { ElementPlaceholder } from '../../types/common';
import { SMART_ANSWER_LABEL_TYPES } from '../../utils/constants';
import { useSelectSmartAnswer } from '../../utils/hooks';
import InlineExtractionIcon from '../smartQuestionComponents/InlineExtractionIcon';
import SnapShotButton from '../smartQuestionComponents/SnapshotButton';
import ProcessedText from './ProcessedText';

type StandardFormControlProps = {
  field?: any;
  children?: React.ReactNode;
  name?: string;
  label?: string;
  rules?: any;
  infoToolTip?: string;
  renderLabelMarkup?: boolean;
  error?: {
    hasError?: boolean;
    errorMessage?: string;
  };
  onFocus?: (...args: any[]) => any;
  padding?: string;
  backgroundColor?: string;
  showIsRequired?: boolean;
  smartAnswer?: any;
  smartAnswerGuid?: string;
  smartAnswerLayout?: string;
  smartAnswerSx?: React.CSSProperties;
  errorPosition?: 'absolute' | 'relative';
  maxRowWidth?: 'string';
  placeholders?: ElementPlaceholder[];
  hint?: string;
  whiteSpace?: string;
  labelSize?: string;
};

/**
 * @typedef {object} StandardFormControlProps
 * @property {object} error - The error message.
 * @property {string}[ backgroundColor="none"] - The background color.
 * @property {node} children - The children components.
 * @property {function} onFocus - The onFocus function.
 * @property {string} padding - The padding.
 *
 * @param {StandardFormControlProps} props
 * @returns
 */
export default function StandardFormControl({
  error,
  backgroundColor = 'none',
  children,
  onFocus = null,
  padding = '1rem 0.25rem',
  smartAnswerLayout = SMART_ANSWER_LABEL_TYPES.SNAPSHOT_BUTTON_AND_ICON,
  errorPosition = 'relative',
  renderLabelMarkup = false,
  maxRowWidth,
  ...question
}: Readonly<StandardFormControlProps>) {
  const {
    name,
    label,
    rules,
    infoToolTip,
    showIsRequired = true,
    placeholders,
    hint,
  } = question;

  const storeSmartAnswer = useSelectSmartAnswer(
    question.smartAnswerGuid ?? name,
  );
  const smartAnswer =
    storeSmartAnswer && Object.keys(storeSmartAnswer).length > 0
      ? storeSmartAnswer
      : question.smartAnswer;

  const checkSmartAnswerLayout = (type) => smartAnswerLayout === type;

  return (
    <FormControl
      isInvalid={error}
      isRequired={(rules?.required && showIsRequired) || false}
      onFocus={onFocus}
      backgroundColor={backgroundColor}
      padding={padding}
      position={'relative'}
      width={maxRowWidth}
      maxWidth={maxRowWidth}
    >
      <Flex
        marginBottom={'0.25rem'}
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex className="label" align="center">
          <FormLabel
            sx={{
              display: 'inline',
              marginInlineEnd: 0,
              whiteSpace: question.whiteSpace,
            }}
            fontSize={question.labelSize ?? 'md'}
            id={`${name}-label`}
            mb={0}
          >
            {label &&
              (renderLabelMarkup ? (
                parseLabelMarkup(label)
              ) : (
                <ProcessedText text={label} placeholders={placeholders} />
              ))}
          </FormLabel>
          {infoToolTip && <GenericTooltip label={infoToolTip} />}
        </Flex>
        {checkSmartAnswerLayout(
          SMART_ANSWER_LABEL_TYPES.SNAPSHOT_BUTTON_ONLY,
        ) && (
          <SnapShotButton
            snippets={smartAnswer?.aiResponse?.snippets}
            questionName={name}
          />
        )}
        {checkSmartAnswerLayout(SMART_ANSWER_LABEL_TYPES.ICON_ONLY) && (
          <InlineExtractionIcon
            testId={`${name}-extraction-icon-${smartAnswer?.iconType}`}
            iconType={smartAnswer?.iconType}
          />
        )}
        {checkSmartAnswerLayout(
          SMART_ANSWER_LABEL_TYPES.SNAPSHOT_BUTTON_AND_ICON,
        ) && (
          <Flex className="smartAnswerIcons" align={'center'} gap={'0.75rem'}>
            <ExtractionDataIcon
              testId={`${name}-extraction-icon-${smartAnswer?.iconType}`}
              iconType={smartAnswer?.iconType}
              sx={question.smartAnswerSx ?? {}}
            />
            <SnapShotButton
              snippets={smartAnswer?.aiResponse?.snippets}
              questionName={name}
            />
          </Flex>
        )}
      </Flex>

      {children}
      {hint && (
        <ProcessedText
          text={hint}
          placeholders={placeholders}
          fontSize="sm"
          color="gray.600"
          sx={{
            display: 'block',
          }}
        />
      )}
      {error.hasError && (
        <FormErrorMessage
          style={{
            position: errorPosition,
          }}
        >
          {error.errorMessage}
        </FormErrorMessage>
      )}
    </FormControl>
  );
}
